import React, { useEffect, useState, useContext } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import brLocale from "date-fns/locale/pt-BR";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Button, Grid, TextField, Typography, Box, CircularProgress, Divider, Stack, Chip } from "@mui/material";
import api from "../../services/api";
import { format } from "date-fns";
import { toast } from "react-toastify";
import axios from "axios";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

// 🔹 Tema v4 (igual ao login) para pegar a cor whitelabel
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
// 🔹 Mantemos o tema v5 para demais tokens (tooltip, textos, etc.)
import { useTheme as useThemeV5 } from "@mui/material/styles";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export const ChartsDate = ({ isOperationalUser = false }) => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const [initialDate, setInitialDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [finalDate, setFinalDate] = useState(new Date());
  const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    if (companyId) handleGetTicketsInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleGetTicketsInformation = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(
        `/dashboard/ticketsDay?initialDate=${format(initialDate, "yyyy-MM-dd")}&finalDate=${format(finalDate, "yyyy-MM-dd")}&companyId=${companyId}`
      );
      setTicketsData(data);
    } catch (error) {
      const status = error?.response?.status;
      const requestCanceled =
        axios.isCancel(error) || error?.code === "ERR_CANCELED";
      const authSessionMissing =
        !localStorage.getItem("token") ||
        !localStorage.getItem("refreshToken");
      const isLoginScreen = window.location.pathname === "/login";
      const ignoreError =
        requestCanceled ||
        authSessionMissing ||
        isLoginScreen ||
        status === 401 ||
        status === 403;

      if (!ignoreError) {
        toast.error(i18n.t("dashboard.charts.errorFetchingTickets"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: themeV5.palette.background.paper,
        titleColor: themeV5.palette.text.primary,
        bodyColor: themeV5.palette.text.secondary,
        borderColor: themeV5.palette.divider,
        borderWidth: 1,
        boxPadding: 8,
        padding: 10,
        usePointStyle: true,
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: themeV5.palette.divider } },
    },
  };

  const dataCharts = {
    labels: ticketsData?.data.length > 0 ? ticketsData.data.map(item => (item.hasOwnProperty("horario") ? `${item.horario}:00` : item.data)) : [],
    datasets: [
      {
        label: i18n.t("dashboard.charts.ticketsLabel"),
        data: ticketsData?.data.length > 0 ? ticketsData.data.map(item => item.total) : [],
        borderColor: PRIMARY_MAIN,
        backgroundColor: "rgba(0,0,0,0.06)", // preenchimento leve neutro (evita tingir de azul quando o primary não é azul)
        tension: 0.4,
        fill: true,
      },
    ],
  };

  return (
    <Box sx={{ p: 0, background: "transparent" }}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", lg: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography component="h2" variant="h5" fontWeight={800} color="text.primary" sx={{ mb: 0.5 }}>
            {isOperationalUser ? "Mis atenciones" : i18n.t("dashboard.users.totalAttendances")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isOperationalUser
              ? "Evolucion diaria de sus tickets atendidos en el rango seleccionado."
              : "Evolucion diaria de los atendimientos en el rango seleccionado."}
          </Typography>
        </Box>
        <Chip
          label={i18n.t("dashboard.users.totalLabel", { count: ticketsData?.count || 0 })}
          sx={{
            px: 1.5,
            py: 2.5,
            fontSize: "0.95rem",
            fontWeight: 800,
            color: PRIMARY_CONTRAST,
            background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
            boxShadow: "0 12px 24px rgba(17,82,147,0.18)"
          }}
        />
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 4,
          backgroundColor: "rgba(15,23,42,0.03)",
          border: "1px solid rgba(15,23,42,0.05)"
        }}
      >
        <Grid item xs={12} sm={6} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
            <DatePicker
              value={initialDate}
              onChange={newValue => setInitialDate(newValue)}
              label={i18n.t("dashboard.date.initialDate")}
              renderInput={params => <TextField {...params} fullWidth variant="outlined" size="small" />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
            <DatePicker
              value={finalDate}
              onChange={newValue => setFinalDate(newValue)}
              label={i18n.t("dashboard.date.finalDate")}
              renderInput={params => <TextField {...params} fullWidth variant="outlined" size="small" />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md>
          <Button
            onClick={handleGetTicketsInformation}
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{
              background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
              color: PRIMARY_CONTRAST,
              transition: "all .2s ease-in-out",
              borderRadius: "14px",
              py: 1.15,
              fontWeight: 700,
              boxShadow: "0 14px 28px rgba(17,82,147,0.18)",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 18px 34px rgba(17,82,147,0.24)"
              }
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : i18n.t("dashboard.buttons.filter")}
          </Button>
        </Grid>
      </Grid>

      <Box
        sx={{
          height: 360,
          position: "relative",
          p: { xs: 1, md: 2 },
          borderRadius: 4,
          background: "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(244,247,251,0.95))",
          border: "1px solid rgba(15,23,42,0.05)"
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : ticketsData?.data.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "grey.100", borderRadius: 2 }}>
            <Typography color="textSecondary">{i18n.t("dashboard.charts.noDataAvailable")}</Typography>
          </Box>
        ) : (
          <Line options={options} data={dataCharts} />
        )}
      </Box>
    </Box>
  );
};

export default ChartsDate;
